import { create } from "@/store";
import { subscribeWithSelector } from "zustand/middleware";
import { dataSyncEmitter } from "@/modules/events/emitter";

type CameraState = {
  stream: MediaStream | null;
  activeFaceJid: null | string;
  cameraList: MediaDeviceInfo[] | null;
  cameraId: string | null;
  microphoneId: string | null;
  loading: boolean;
  error: "no_video_element" | "no_audio" | "no_video" | "permission_denied" | "failure" | null;
  openCvState: "ready" | "loading" | "error";
  isRecording: boolean;
};
export type CameraStore = CameraState & {
  setStream(stream: MediaStream): void;
  destroyTracks(): void;
  updateTrack(track: MediaStreamTrack): void;
  setActiveFaceJid(activeFaceJid: string | null): void;
  setCameraList(cameraList: MediaDeviceInfo[]): void;
  setCameraId(cameraId: string): void;
  setMicrophoneId(microphoneId: string): void;
  setLoading(loading: boolean): void;
  setError(error: CameraState["error"]): void;
  setOpenCvState(state: CameraState["openCvState"]): void;
  setIsRecording(isRecording: boolean): void;
  reset(): void;
};

const initialState: CameraState = {
  stream: null,
  activeFaceJid: null,
  cameraList: null,
  cameraId: null,
  microphoneId: null,
  loading: true,
  error: null,
  openCvState: "loading",
  isRecording: false
};

const useCamera = create<CameraStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    setStream(stream) {
      set({ stream });
    },
    destroyTracks() {
      const stream = get().stream;
      if (!stream) return;
      stream.getTracks().forEach((track) => {
        track.stop();
        track.enabled = false;
        stream.removeTrack(track);
      });
    },
    setMicrophoneId(microphoneId) {
      set({ microphoneId });
    },
    updateTrack(track) {
      const stream = get().stream;
      if (!stream) {
        if (isDev) log.err(`Can't update track, stream is`, stream);
        return;
      }
      const oldTrack = stream.getTracks().find((t) => t.kind === track.kind);
      if (oldTrack) {
        oldTrack.stop();
        oldTrack.enabled = false;
        stream.removeTrack(oldTrack);
      }
      stream.addTrack(track);
      if (track.kind === "video") dataSyncEmitter.emit("video-track-update", track);
    },
    setActiveFaceJid(activeFaceJid) {
      set({ activeFaceJid });
    },
    setCameraList(cameraList) {
      set({ cameraList });
    },
    setCameraId(cameraId) {
      set({ cameraId });
    },
    setLoading(loading) {
      set({ loading });
    },
    setError(error) {
      set({ error });
    },
    setOpenCvState(state) {
      set({ openCvState: state });
    },
    setIsRecording(isRecording) {
      set({ isRecording });
    },
    reset() {
      set(initialState);
    }
  }))
);

const cameraStore = useCamera.getState;

export { useCamera, cameraStore };
